import { CountryCode, ISOCurrencyCode } from '@policyfly/protobuf'
import { Role } from '@policyfly/protobuf/pfbingo'

/**
 * A stand-in value for Infinity which can be parsed without returning NaN.
 */
export const INFINITY = 9999999999

/**
 * The flag path that when set to true will force defaults to run within a form even if loading from previous data.
 */
export const FORM_INITIAL_FLAG_PATH = 'initialFormFlag'

/**
 * The number of bands BCAPD supports.
 */
export const BCAPD_BAND_COUNT = 5

/**
 * The minimum TIV needed for an application to not be declined on BCAPD.
 */
export const BCAPD_DECLINATION_VALUE = 25000

/**
 * Keys used for the FormAddress data structure.
 */
export const ADDRESS_PARTS = ['address', 'address2', 'city', 'county', 'state', 'zip', 'country', 'geocoderLocationType', 'googlePlaceId'] as const

type USState = Readonly<{
  name: string
  abbreviation: string
  zips?: ReadonlyArray<string>
}>
/**
 * Information about US States.
 */
export const US_STATES: ReadonlyArray<USState> = [
  {
    name: 'Alabama',
    abbreviation: 'AL',
    zips: [
      '36532',
      '36526',
      '36535',
      '36507',
      '36527',
      '36542',
      '36567',
      '36551',
      '36561',
      '36530',
      '36549',
      '36580',
      '36576',
      '36578',
      '36562',
      '36579',
      '36574',
      '36555',
      '36511',
      '36559',
      '36550',
      '36564',
      '36502',
      '36426',
      '36441',
      '36543',
      '36695',
      '36608',
      '36605',
      '36582',
      '36609',
      '36575',
      '36693',
      '36606',
      '36618',
      '36541',
      '36619',
      '36571',
      '36613',
      '36617',
      '36544',
      '36610',
      '36587',
      '36603',
      '36604',
      '36522',
      '36607',
      '36572',
      '36521',
      '36611',
      '36612',
      '36560',
      '36523',
      '36509',
      '36525',
      '36505',
      '36528',
      '36602',
      '36688',
      '36568',
      '36512',
      '36616',
      '36590',
      '36615',
    ],
  },
  { name: 'Alaska', abbreviation: 'AK' },
  { name: 'American Samoa', abbreviation: 'AS' },
  { name: 'Arizona', abbreviation: 'AZ' },
  { name: 'Arkansas', abbreviation: 'AR' },
  { name: 'California', abbreviation: 'CA' },
  { name: 'Colorado', abbreviation: 'CO' },
  { name: 'Connecticut', abbreviation: 'CT' },
  { name: 'Delaware', abbreviation: 'DE' },
  { name: 'District Of Columbia', abbreviation: 'DC' },
  { name: 'Federated States Of Micronesia', abbreviation: 'FM' },
  {
    name: 'Florida',
    abbreviation: 'FL',
    zips: [
      '33009',
      '33020',
      '33023',
      '33024',
      '33025',
      '33026',
      '33029',
      '33063',
      '33064',
      '33066',
      '33068',
      '33069',
      '33071',
      '33076',
      '33304',
      '33309',
      '33311',
      '33313',
      '33314',
      '33319',
      '33322',
      '33324',
      '33325',
      '33327',
      '33330',
      '33332',
      '33010',
      '33012',
      '33014',
      '33016',
      '33034',
      '33035',
      '33056',
      '33127',
      '33129',
      '33130',
      '33131',
      '33140',
      '33142',
      '33144',
      '33145',
      '33147',
      '33158',
      '33161',
      '33162',
      '33165',
      '33167',
      '33174',
      '33176',
      '33179',
      '33183',
      '33187',
      '33189',
      '33196',
      '33405',
      '33409',
      '33410',
      '33411',
      '33414',
      '33415',
      '33418',
      '33428',
      '33430',
      '33436',
      '33438',
      '33446',
      '33458',
      '33460',
      '33467',
      '33469',
      '33476',
      '33484',
      '33486',
      '33496',
    ],
  },
  { name: 'Georgia', abbreviation: 'GA' },
  { name: 'Guam', abbreviation: 'GU' },
  { name: 'Hawaii', abbreviation: 'HI' },
  { name: 'Idaho', abbreviation: 'ID' },
  { name: 'Illinois', abbreviation: 'IL' },
  { name: 'Indiana', abbreviation: 'IN' },
  { name: 'Iowa', abbreviation: 'IA' },
  { name: 'Kansas', abbreviation: 'KS' },
  { name: 'Kentucky', abbreviation: 'KY' },
  {
    name: 'Louisiana',
    abbreviation: 'LA',
    zips: [
      '70030',
      '70036',
      '70037',
      '70038',
      '70040',
      '70041',
      '70050',
      '70067',
      '70070',
      '70082',
      '70083',
      '70091',
      '70343',
      '70344',
      '70345',
      '70353',
      '70354',
      '70355',
      '70357',
      '70358',
      '70360',
      '70363',
      '70364',
      '70373',
      '70374',
      '70375',
      '70377',
      '70394',
      '70397',
    ],
  },
  { name: 'Maine', abbreviation: 'ME' },
  { name: 'Marshall Islands', abbreviation: 'MH' },
  { name: 'Maryland', abbreviation: 'MD' },
  { name: 'Massachusetts', abbreviation: 'MA' },
  { name: 'Michigan', abbreviation: 'MI' },
  { name: 'Minnesota', abbreviation: 'MN' },
  {
    name: 'Mississippi',
    abbreviation: 'MS',
    zips: [
      '39520',
      '39525',
      '39556',
      '39572',
      '39576',
      '39501',
      '39503',
      '39507',
      '39530',
      '39531',
      '39532',
      '39534',
      '39540',
      '39560',
      '39571',
      '39574',
      '39553',
      '39562',
      '39563',
      '39564',
      '39565',
      '39567',
      '39581',
    ],
  },
  { name: 'Missouri', abbreviation: 'MO' },
  { name: 'Montana', abbreviation: 'MT' },
  { name: 'Nebraska', abbreviation: 'NE' },
  { name: 'Nevada', abbreviation: 'NV' },
  { name: 'New Hampshire', abbreviation: 'NH' },
  { name: 'New Jersey', abbreviation: 'NJ' },
  { name: 'New Mexico', abbreviation: 'NM' },
  { name: 'New York', abbreviation: 'NY' },
  { name: 'North Carolina', abbreviation: 'NC' },
  { name: 'North Dakota', abbreviation: 'ND' },
  { name: 'Northern Mariana Islands', abbreviation: 'MP' },
  { name: 'Ohio', abbreviation: 'OH' },
  { name: 'Oklahoma', abbreviation: 'OK' },
  { name: 'Oregon', abbreviation: 'OR' },
  { name: 'Palau', abbreviation: 'PW' },
  { name: 'Pennsylvania', abbreviation: 'PA' },
  { name: 'Puerto Rico', abbreviation: 'PR' },
  { name: 'Rhode Island', abbreviation: 'RI' },
  { name: 'South Carolina', abbreviation: 'SC' },
  { name: 'South Dakota', abbreviation: 'SD' },
  { name: 'Tennessee', abbreviation: 'TN' },
  { name: 'Texas', abbreviation: 'TX' },
  { name: 'Utah', abbreviation: 'UT' },
  { name: 'Vermont', abbreviation: 'VT' },
  { name: 'Virgin Islands', abbreviation: 'VI' },
  { name: 'Virginia', abbreviation: 'VA' },
  { name: 'Washington', abbreviation: 'WA' },
  { name: 'West Virginia', abbreviation: 'WV' },
  { name: 'Wisconsin', abbreviation: 'WI' },
  { name: 'Wyoming', abbreviation: 'WY' },
]

/**
 * An alternative mapping of {@link US_STATES} of zips to state.
 */
export const ZIP_STATE_MAP: Readonly<Record<string, USState>> = US_STATES.reduce<Record<string, USState>>((acc, state) => {
  if (state.zips) {
    for (const zip of state.zips) {
      acc[zip] = state
    }
  }
  return acc
}, {})

/**
 * List of Canadian Provinces.
 */
export const CA_PROVINCES: ReadonlyArray<string> = [
  'Alberta',
  'British Columbia',
  'Manitoba',
  'New Brunswick',
  'Newfoundland and Labrador',
  'Northwest Territories',
  'Nova Scotia',
  'Nunavut',
  'Ontario',
  'Prince Edward Island',
  'Quebec',
  'Saskatchewan',
  'Yukon',
]

/**
 * The band ranges used for BCAPD.
 */
export const BCAPD_BAND_RANGES = {
  low: '$0 - $99,999',
  high: '$100,000 +',
  combo: '$0 - $100,000 +',
} as const

/**
 * Special keys used on arrays in json data to track their state across applications.
 */
export const ARRAY_DELETION_KEYS = {
  ADDED_ID: 'addedAppId',
  ADDED_KIND: 'addedAppKind',
  DELETED_ID: 'deletedAppId',
  DELETED: 'deleted',
  DELETED_REASON: 'deletedReason',
  COVERAGE: 'coverageType',
} as const

/**
 * The path for the Premium Bearing Endorsement toggle on some programs.
 */
export const PREMIUM_PATH = 'premium.bearing'

/**
 * A regex that splits a key with an index in it (eg. `some_array_0_path`).
 */
export const SPLIT_ON_INDEX_REGEX = /_\d+_/

/**
 * The string used to separate the `low` and `high` from a `range` in a protobuf `Decimal`.
 */
export const DECIMAL_RANGE_SEPARATOR = ' - '

/**
 * Maps each {@link CountryCode} to a friendly string.
 */
export const COUNTRY_CODE_STRING_MAP = new Map<CountryCode, string>([
  [CountryCode.XX, ''],
  [CountryCode.US, 'United States'],
  [CountryCode.CA, 'Canada'],
  [CountryCode.GB, 'Great Britain'],
  [CountryCode.DE, 'Germany'],
  [CountryCode.CH, 'Switzerland'],
  [CountryCode.IE, 'Ireland'],
  [CountryCode.AF, 'Afghanistan'],
  [CountryCode.AL, 'Albania'],
  [CountryCode.DZ, 'Algeria'],
  [CountryCode.AS, 'American Samoa'],
  [CountryCode.AD, 'Andorra'],
  [CountryCode.AO, 'Angola'],
  [CountryCode.AI, 'Anguilla'],
  [CountryCode.AQ, 'Antarctica'],
  [CountryCode.AG, 'Antigua and Barbuda'],
  [CountryCode.AR, 'Argentina'],
  [CountryCode.AM, 'Armenia'],
  [CountryCode.AW, 'Aruba'],
  [CountryCode.AU, 'Australia'],
  [CountryCode.AT, 'Austria'],
  [CountryCode.AZ, 'Azerbaijan'],
  [CountryCode.BS, 'Bahamas'],
  [CountryCode.BH, 'Bahrain'],
  [CountryCode.BD, 'Bangladesh'],
  [CountryCode.BB, 'Barbados'],
  [CountryCode.BY, 'Belarus'],
  [CountryCode.BE, 'Belgium'],
  [CountryCode.BZ, 'Belize'],
  [CountryCode.BJ, 'Benin'],
  [CountryCode.BM, 'Bermuda'],
  [CountryCode.BT, 'Bhutan'],
  [CountryCode.BO, 'Bolivia'],
  [CountryCode.BA, 'Bosnia and Herzegovina'],
  [CountryCode.BW, 'Botswana'],
  [CountryCode.BR, 'Brazil'],
  [CountryCode.IO, 'British Indian Ocean Territory'],
  [CountryCode.BN, 'Brunei Darussalam'],
  [CountryCode.BG, 'Bulgaria'],
  [CountryCode.BF, 'Burkina Faso'],
  [CountryCode.BI, 'Burundi'],
  [CountryCode.KH, 'Cambodia'],
  [CountryCode.CM, 'Cameroon'],
  [CountryCode.CV, 'Cape Verde'],
  [CountryCode.KY, 'Cayman Islands'],
  [CountryCode.CF, 'Central African Republic'],
  [CountryCode.TD, 'Chad'],
  [CountryCode.CL, 'Chile'],
  [CountryCode.CN, 'China'],
  [CountryCode.CX, 'Christmas Island'],
  [CountryCode.CC, 'Cocos (Keeling) Islands'],
  [CountryCode.CO, 'Colombia'],
  [CountryCode.KM, 'Comoros'],
  [CountryCode.CD, 'Democratic Republic of the Congo'],
  [CountryCode.CG, 'Congo'],
  [CountryCode.CK, 'Cook Islands'],
  [CountryCode.CR, 'Costa Rica'],
  [CountryCode.CI, "Côte d'Ivoire"],
  [CountryCode.HR, 'Croatia'],
  [CountryCode.CU, 'Cuba'],
  [CountryCode.CW, 'Curaçao'],
  [CountryCode.CY, 'Cyprus'],
  [CountryCode.CZ, 'Czech Republic'],
  [CountryCode.DK, 'Denmark'],
  [CountryCode.DJ, 'Djibouti'],
  [CountryCode.DM, 'Dominica'],
  [CountryCode.DO, 'Dominican Republic'],
  [CountryCode.EC, 'Ecuador'],
  [CountryCode.EG, 'Egypt'],
  [CountryCode.SV, 'El Salvador'],
  [CountryCode.GQ, 'Equatorial Guinea'],
  [CountryCode.ER, 'Eritrea'],
  [CountryCode.EE, 'Estonia'],
  [CountryCode.SZ, 'Eswatini'],
  [CountryCode.ET, 'Ethiopia'],
  [CountryCode.FK, 'Falkland Islands (Malvinas)'],
  [CountryCode.FO, 'Faroe Islands'],
  [CountryCode.FJ, 'Fiji'],
  [CountryCode.FI, 'Finland'],
  [CountryCode.FR, 'France'],
  [CountryCode.GF, 'French Guiana'],
  [CountryCode.PF, 'French Polynesia'],
  [CountryCode.TF, 'French Southern Territories'],
  [CountryCode.GA, 'Gabon'],
  [CountryCode.GM, 'Gambia'],
  [CountryCode.GE, 'Georgia'],
  [CountryCode.GH, 'Ghana'],
  [CountryCode.GI, 'Gibraltar'],
  [CountryCode.GR, 'Greece'],
  [CountryCode.GL, 'Greenland'],
  [CountryCode.GD, 'Grenada'],
  [CountryCode.GP, 'Guadeloupe'],
  [CountryCode.GU, 'Guam'],
  [CountryCode.GT, 'Guatemala'],
  [CountryCode.GG, 'Guernsey'],
  [CountryCode.GN, 'Guinea'],
  [CountryCode.GW, 'Guinea-Bissau'],
  [CountryCode.GY, 'Guyana'],
  [CountryCode.HT, 'Haiti'],
  [CountryCode.VA, 'Holy See (Vatican City State)'],
  [CountryCode.HN, 'Honduras'],
  [CountryCode.HK, 'Hong Kong'],
  [CountryCode.HU, 'Hungary'],
  [CountryCode.IS, 'Iceland'],
  [CountryCode.IN, 'India'],
  [CountryCode.ID, 'Indonesia'],
  [CountryCode.IR, 'Iran'],
  [CountryCode.IQ, 'Iraq'],
  [CountryCode.IM, 'Isle of Man'],
  [CountryCode.IL, 'Israel'],
  [CountryCode.IT, 'Italy'],
  [CountryCode.JM, 'Jamaica'],
  [CountryCode.JP, 'Japan'],
  [CountryCode.JE, 'Jersey'],
  [CountryCode.JO, 'Jordan'],
  [CountryCode.KZ, 'Kazakhstan'],
  [CountryCode.KE, 'Kenya'],
  [CountryCode.KI, 'Kiribati'],
  [CountryCode.KP, 'North Korea'],
  [CountryCode.KR, 'South Korea'],
  [CountryCode.KW, 'Kuwait'],
  [CountryCode.KG, 'Kyrgyzstan'],
  [CountryCode.LA, 'Laos'],
  [CountryCode.LV, 'Latvia'],
  [CountryCode.LB, 'Lebanon'],
  [CountryCode.LS, 'Lesotho'],
  [CountryCode.LR, 'Liberia'],
  [CountryCode.LY, 'Libya'],
  [CountryCode.LI, 'Liechtenstein'],
  [CountryCode.LT, 'Lithuania'],
  [CountryCode.LU, 'Luxembourg'],
  [CountryCode.MO, 'Macao'],
  [CountryCode.MG, 'Madagascar'],
  [CountryCode.MW, 'Malawi'],
  [CountryCode.MY, 'Malaysia'],
  [CountryCode.MV, 'Maldives'],
  [CountryCode.ML, 'Mali'],
  [CountryCode.MT, 'Malta'],
  [CountryCode.MH, 'Marshall Islands'],
  [CountryCode.MQ, 'Martinique'],
  [CountryCode.MR, 'Mauritania'],
  [CountryCode.MU, 'Mauritius'],
  [CountryCode.MX, 'Mexico'],
  [CountryCode.FM, 'Micronesia'],
  [CountryCode.MD, 'Moldova'],
  [CountryCode.MC, 'Monaco'],
  [CountryCode.MN, 'Mongolia'],
  [CountryCode.ME, 'Montenegro'],
  [CountryCode.MS, 'Montserrat'],
  [CountryCode.MA, 'Morocco'],
  [CountryCode.MZ, 'Mozambique'],
  [CountryCode.MM, 'Myanmar'],
  [CountryCode.NA, 'Namibia'],
  [CountryCode.NR, 'Nauru'],
  [CountryCode.NP, 'Nepal'],
  [CountryCode.NL, 'Netherlands'],
  [CountryCode.NC, 'New Caledonia'],
  [CountryCode.NZ, 'New Zealand'],
  [CountryCode.NI, 'Nicaragua'],
  [CountryCode.NE, 'Niger'],
  [CountryCode.NG, 'Nigeria'],
  [CountryCode.NO, 'Norway'],
  [CountryCode.OM, 'Oman'],
  [CountryCode.PK, 'Pakistan'],
  [CountryCode.PW, 'Palau'],
  [CountryCode.PS, 'Palestine, State of'],
  [CountryCode.PA, 'Panama'],
  [CountryCode.PG, 'Papua New Guinea'],
  [CountryCode.PY, 'Paraguay'],
  [CountryCode.PE, 'Peru'],
  [CountryCode.PH, 'Philippines'],
  [CountryCode.PL, 'Poland'],
  [CountryCode.PT, 'Portugal'],
  [CountryCode.PR, 'Puerto Rico'],
  [CountryCode.QA, 'Qatar'],
  [CountryCode.RE, 'Réunion'],
  [CountryCode.RO, 'Romania'],
  [CountryCode.RU, 'Russia'],
  [CountryCode.RW, 'Rwanda'],
  [CountryCode.KN, 'Saint Kitts and Nevis'],
  [CountryCode.LC, 'Saint Lucia'],
  [CountryCode.VC, 'Saint Vincent and the Grenadines'],
  [CountryCode.WS, 'Samoa'],
  [CountryCode.SM, 'San Marino'],
  [CountryCode.ST, 'Sao Tome and Principe'],
  [CountryCode.SA, 'Saudi Arabia'],
  [CountryCode.SN, 'Senegal'],
  [CountryCode.RS, 'Serbia'],
  [CountryCode.SC, 'Seychelles'],
  [CountryCode.SL, 'Sierra Leone'],
  [CountryCode.SG, 'Singapore'],
  [CountryCode.SK, 'Slovakia'],
  [CountryCode.SI, 'Slovenia'],
  [CountryCode.SB, 'Solomon Islands'],
  [CountryCode.SO, 'Somalia'],
  [CountryCode.ZA, 'South Africa'],
  [CountryCode.SS, 'South Sudan'],
  [CountryCode.ES, 'Spain'],
  [CountryCode.LK, 'Sri Lanka'],
  [CountryCode.SD, 'Sudan'],
  [CountryCode.SR, 'Suriname'],
  [CountryCode.SE, 'Sweden'],
  [CountryCode.SY, 'Syria'],
  [CountryCode.TW, 'Taiwan'],
  [CountryCode.TJ, 'Tajikistan'],
  [CountryCode.TZ, 'Tanzania'],
  [CountryCode.TH, 'Thailand'],
  [CountryCode.TL, 'Timor-Leste'],
  [CountryCode.TG, 'Togo'],
  [CountryCode.TO, 'Tonga'],
  [CountryCode.TT, 'Trinidad and Tobago'],
  [CountryCode.TN, 'Tunisia'],
  [CountryCode.TR, 'Turkey'],
  [CountryCode.TM, 'Turkmenistan'],
  [CountryCode.TV, 'Tuvalu'],
  [CountryCode.UG, 'Uganda'],
  [CountryCode.UA, 'Ukraine'],
  [CountryCode.AE, 'United Arab Emirates'],
  [CountryCode.UY, 'Uruguay'],
  [CountryCode.UZ, 'Uzbekistan'],
  [CountryCode.VU, 'Vanuatu'],
  [CountryCode.VE, 'Venezuela'],
  [CountryCode.VN, 'Vietnam'],
  [CountryCode.YE, 'Yemen'],
  [CountryCode.ZM, 'Zambia'],
  [CountryCode.ZW, 'Zimbabwe'],
])

/**
 * Maps each {@link ISOCurrencyCode} to a friendly string.
 */
export const ISO_CURRENCY_CODE_STRING_MAP = new Map<ISOCurrencyCode, string>([
  [ISOCurrencyCode.ISO_CURRENCY_CODE_UNSPECIFIED, ''],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_AED, 'AED'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_AFN, 'AFN'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_ALL, 'ALL'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_AMD, 'AMD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_ANG, 'ANG'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_AOA, 'AOA'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_ARS, 'ARS'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_AUD, 'AUD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_AWG, 'AWG'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_AZN, 'AZN'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_BAM, 'BAM'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_BBD, 'BBD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_BDT, 'BDT'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_BGN, 'BGN'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_BHD, 'BHD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_BIF, 'BIF'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_BMD, 'BMD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_BND, 'BND'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_BOB, 'BOB'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_BRL, 'BRL'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_BSD, 'BSD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_BTN, 'BTN'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_BWP, 'BWP'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_BYN, 'BYN'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_BZD, 'BZD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_CAD, 'CAD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_CDF, 'CDF'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_CHE, 'CHE'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_CHF, 'CHF'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_CHW, 'CHW'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_CLF, 'CLF'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_CLP, 'CLP'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_CNY, 'CNY'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_COP, 'COP'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_COU, 'COU'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_CRC, 'CRC'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_CUC, 'CUC'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_CUP, 'CUP'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_CVE, 'CVE'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_CZK, 'CZK'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_DJF, 'DJF'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_DKK, 'DKK'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_DOP, 'DOP'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_DZD, 'DZD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_EGP, 'EGP'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_ERN, 'ERN'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_ETB, 'ETB'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_EUR, 'EUR'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_FJD, 'FJD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_FKP, 'FKP'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_GBP, 'GBP'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_GEL, 'GEL'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_GGP, 'GGP'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_GHS, 'GHS'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_GIP, 'GIP'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_GMD, 'GMD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_GNF, 'GNF'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_GTQ, 'GTQ'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_GYD, 'GYD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_HKD, 'HKD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_HNL, 'HNL'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_HRK, 'HRK'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_HTG, 'HTG'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_HUF, 'HUF'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_IDR, 'IDR'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_ILS, 'ILS'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_IMP, 'IMP'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_INR, 'INR'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_IQD, 'IQD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_IRR, 'IRR'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_ISK, 'ISK'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_JEP, 'JEP'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_JMD, 'JMD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_JOD, 'JOD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_JPY, 'JPY'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_KES, 'KES'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_KGS, 'KGS'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_KHR, 'KHR'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_KID, 'KID'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_KMF, 'KMF'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_KPW, 'KPW'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_KRW, 'KRW'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_KWD, 'KWD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_KYD, 'KYD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_KZT, 'KZT'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_LAK, 'LAK'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_LBP, 'LBP'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_LKR, 'LKR'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_LRD, 'LRD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_LSL, 'LSL'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_LYD, 'LYD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_MAD, 'MAD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_MDL, 'MDL'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_MGA, 'MGA'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_MKD, 'MKD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_MMK, 'MMK'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_MNT, 'MNT'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_MOP, 'MOP'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_MRU, 'MRU'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_MUR, 'MUR'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_MVR, 'MVR'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_MWK, 'MWK'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_MXN, 'MXN'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_MXV, 'MXV'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_MYR, 'MYR'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_MZN, 'MZN'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_NAD, 'NAD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_NGN, 'NGN'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_NIO, 'NIO'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_NOK, 'NOK'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_NPR, 'NPR'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_NZD, 'NZD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_OMR, 'OMR'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_PAB, 'PAB'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_PEN, 'PEN'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_PGK, 'PGK'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_PHP, 'PHP'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_PKR, 'PKR'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_PLN, 'PLN'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_PYG, 'PYG'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_QAR, 'QAR'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_RON, 'RON'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_RSD, 'RSD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_RUB, 'RUB'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_RWF, 'RWF'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_SAR, 'SAR'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_SBD, 'SBD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_SCR, 'SCR'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_SDG, 'SDG'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_SEK, 'SEK'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_SGD, 'SGD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_SHP, 'SHP'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_SLL, 'SLL'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_SOS, 'SOS'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_SRD, 'SRD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_SSP, 'SSP'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_STN, 'STN'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_SVC, 'SVC'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_SYP, 'SYP'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_SZL, 'SZL'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_THB, 'THB'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_TJS, 'TJS'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_TMT, 'TMT'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_TND, 'TND'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_TOP, 'TOP'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_TRY, 'TRY'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_TTD, 'TTD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_TVD, 'TVD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_TWD, 'TWD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_TZS, 'TZS'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_UAH, 'UAH'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_UGX, 'UGX'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_USD, 'USD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_UYU, 'UYU'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_UZS, 'UZS'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_VES, 'VES'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_VND, 'VND'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_VUV, 'VUV'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_WST, 'WST'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_XAF, 'XAF'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_XAG, 'XAG'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_XAU, 'XAU'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_XBA, 'XBA'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_XBB, 'XBB'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_XBC, 'XBC'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_XBD, 'XBD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_XCD, 'XCD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_XDR, 'XDR'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_XOF, 'XOF'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_XPD, 'XPD'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_XPF, 'XPF'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_XPT, 'XPT'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_XSU, 'XSU'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_XTS, 'XTS'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_XUA, 'XUA'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_XXX, 'XXX'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_YER, 'YER'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_ZAR, 'ZAR'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_ZMW, 'ZMW'],
  [ISOCurrencyCode.ISO_CURRENCY_CODE_ZWL, 'ZWL'],
])

/**
 * A specific key used to identify the variants in a quote from the legacy rater.
 */
export const VARIANTS_KEY = '$variants'
/**
 * A specific key used to identify the values in variants in a quote from the legacy rater.
 */
export const VARIANTS_VALUES_KEY = 'values'
/**
 * The different rating contexts available for the legacy rater.
 */
export const RATING_CONTEXTS = ['submit', 'initialQuote', 'quote', 'license', 'binder'] as const

/**
 * The file extensions known to attachment components.
 */
export const KNOWN_FILE_EXTENSIONS = new Set([
  // Documents
  'pdf',
  'doc',
  'docx',
  'rtf',
  'txt',
  'md',
  // Spreadsheets / data
  'csv',
  'xls',
  'xlsx',
  'ods',
  // Presentations
  'ppt',
  'pptx',
  'odp',
  // Images
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'heic',
  'tif',
  'tiff',
  // Email exports
  'msg',
  'eml',
  // Archives
  'zip',
  '7z',
  'rar',
  'tar',
  'gz',
  'bz2',
  'xz',
  // Multipart / technical
  'd',
  'ts',
  'map',
  // Signatures / certificates
  'p7s',
  'p7m',
  'pem',
  'crt',
  // CAD / engineering attachments
  'dwg',
  'dxf',
  'acord',
  'xml',
  'json',
  'ext',
])

/**
 * Roles that can be held by partners/agents.
 */
export const PARTNER_ROLES: readonly Role[] = [
  Role.ROLE_AGENCY_READ_ONLY,
  Role.ROLE_DISTRIBUTION_PARTNER,
  Role.ROLE_ISSUING_AGENT,
  Role.ROLE_QUOTING_ISSUING_AGENT,
]

/**
 * Roles that can be held by internal users.
 */
export const INTERNAL_ROLES: readonly Role[] = [
  Role.ROLE_PROGRAM_READ_ONLY,
  Role.ROLE_AUDITOR,
  Role.ROLE_AUDITOR_GLOBAL,
  Role.ROLE_TECHNICIAN,
  Role.ROLE_QUALITY_CONTROL,
  Role.ROLE_UNDERWRITER,
  Role.ROLE_PROGRAM_ADMIN,
]

/**
 * Read Only internal roles, which can view a program's records but not act on them.
 */
export const AUDITOR_ROLES: readonly Role[] = [
  Role.ROLE_AUDITOR,
  Role.ROLE_AUDITOR_GLOBAL,
]

/**
 * Roles that can be held by admin/staff.
 */
export const ADMIN_ROLES: readonly Role[] = [
  Role.ROLE_STAFF,
  Role.ROLE_SYSTEM_READ_ONLY,
  Role.ROLE_SUPERUSER,
]

/**
 * Program roles available in the Capture domain.
 */
export const CAPTURE_ROLES: readonly Role[] = [
  Role.ROLE_CAPTURE_READ_ONLY,
  Role.ROLE_CAPTURE_READ_WRITE,
]

/**
 * Program roles available in the Accounting domain.
 */
export const ACCOUNTING_ROLES: readonly Role[] = [
  Role.ROLE_ACCOUNTING_READ_ONLY,
  Role.ROLE_ACCOUNTING_READ_WRITE,
]

/**
 * Program roles available in the Reporting domain.
 */
export const REPORTING_ROLES: readonly Role[] = [
  Role.ROLE_REPORTING_READ_ONLY,
]

/**
 * Program roles available in the API domain.
 */
export const API_ROLES: readonly Role[] = [
  Role.ROLE_API_AGENCY_ADMIN,
  Role.ROLE_API_OWNER_ADMIN,
]

/**
 * Defines the default ordering of FAB actions.
 * Can be imported to insert a custom item at a certain position.
 *
 * Items are separated by larger numeric gaps to allow inserting
 * custom items between them.
 *
 * @example
 * ```ts
 * // just after the PASS item
 * order: { application: FAB_ORDER.APPLICATION.PASS + 1, ... }
 * // just before the FAIL item
 * order: { application: FAB_ORDER.APPLICATION.FAIL - 1, ... }
 * ```
 */
export const FAB_ORDER = {
  APPLICATION: {
    QUOTE: 10,
    EDIT_APPLICATION: 20,
    EDIT_INFORMATION: 30,
    ADD_INFORMATION: 40,
    REVIEW_QUOTES: 50,
    EDIT_QUOTE: 60,
    ISSUE_BINDER: 70,
    ISSUE_POLICY: 80,
    EDIT_PREMIUM: 90,
    PASS: 100,
    FAIL: 110,
    SKIP: 120,
    OVERRIDE: 130,
    CREATE_ENDORSEMENT: 140,
    APPROVE_ENDORSEMENT: 150,
    ACTIVATE_POLICY: 160,
    RECLAIM: 170,
    REPLICATE_APP: 180,
    START_OVER: 190,
    RESUME_CANCELLATION: 200,
    DISCARD_CANCELLATION: 210,
    PROCESS_CANCELLATION: 220,
    ADD_TASK: 230,
    RECHECK: 240,
    DISCARD_ENDORSEMENT: 250,
    DISCARD_REINSTATEMENT: 260,
    QUOTE_LOST: 270,
    DELETE_APPLICATION: 280,
    ARCHIVE_APPLICATION: 290,
    BACK_TO_DRAFT: 300,
    BACK_TO_REVIEWING: 310,
    ADVANCE_PENDING_ISSUE: 320,
    END_AND_RESTART: 330,
    DECLINE_APPLICATION: 340,
    CANCEL: 350,
    ABORT_CANCELLATION: 360,
  },
  POLICY: {
    START_OVER: 10,
    PROCESS_REINSTATEMENT: 20,
    CREATE_ENDORSEMENT: 30,
    CREATE_EXTENSION: 40,
    RECLAIM: 50,
    ENDORSEMENT_CORRECTION_REPORT: 60,
    RENEW: 70,
    CANCEL: 80,
    REQUEST_CANCELLATION: 90,
    OFFER_RENEWAL: 100,
    DECLINE_RENEWAL: 110,
    ADD_TASK: 120,
    ABORT_CANCELLATION: 130,
  },
  AGENCY_DETAIL: {
    RENAME_AGENCY: 10,
    ADD_AGENT: 20,
    MANAGE_ACCESS: 30,
    ARCHIVE_AGENCY: 40,
  },
  AGENCY_LIST: {
    ADD_AGENCY: 10,
  },
  DOCUMENTS: {
    CREATE_NEW_DOCUMENT: 10,
    CREATE_NEW_REVISION: 20,
    VIEW_CURRENT_REVISION: 30,
  },
  HOME: {
    NEW_APPLICANT: 10,
  },
  USERS: {
    CREATE_USER: 10,
    EDIT: 20,
    RESEND_INVITE: 30,
    RESET_PASSWORD: 40,
    REMOVE: 50,
  },
}
